import { FetchUtil } from "../util/FetchUtil";
import { IUserActivityModel } from "../models/UserActivity/IUserActivityModel";
import { IUserActivityServiceEndpoint } from "../models/UserActivity/IUserActivityServiceEndpoint";
import { IUserActivityViewModel } from "../models/UserActivity/IUserActivityViewModel";
import { IUserProfile } from "../@types/Wyanoke/UserProfile";
import { StringUtil } from "../util/StringUtil";
import { UserActivityModel } from "../models/UserActivity/UserActivityModel";
import { WyanokeUtil } from "../util/WyanokeUtil";

interface ActivityStep {
  Id: string;
  TimeStamp: string | null;
  Title: string;
  Type: string;
  Url: string;
  ParentId: string;
}

interface Activity {
  CommerceMode: any;
  CurrentStepId: string | null;
  Expires: string;
  Id: string;
  Price: number;
  ProductCode: string;
  Providers: any[];
  Specialties: Array<{ Id: string; Title: string }>;
  Topics: any[];
  Collections: any[];
  Steps: ActivityStep[];
  Title: string;
  Url: string;
  LMSName: string;
}

declare global {
  interface Window {
    CombinedActivity: Activity[];
  }
}

export class CombinedActivityService {
  private _currentStatePromise: Promise<IUserActivityViewModel[]> | null = null;
  private _updatePromise: Promise<void> | null = null;

  constructor(private readonly _api: IUserActivityServiceEndpoint) {
    console.log("[CombinedActivityService] Initializing service");
  }

  public async getActivityStatus(activityIds: string[]): Promise<IUserActivityViewModel[]> {
    console.log("[CombinedActivityService] Getting activity status for IDs:", activityIds);

    const profile = await WyanokeUtil.loadUserProfile(true, false);
    if (!profile) {
      console.warn("[CombinedActivityService] No user profile found");
      return [];
    }

    const contactId = profile.contactid || "";
    const endpoint = this._api.load;

    if (!endpoint) {
      console.error("[CombinedActivityService] Could not resolve endpoint");
      throw new Error("Could not resolve endpoint");
    }

    console.log("[CombinedActivityService] Fetching status for activities:", {
      contactId,
      activityCount: activityIds.length,
      timestamp: new Date().toISOString()
    });

    const statusPromises = activityIds.map(async (activityId) => {
      const values = {
        activityId,
        contactId,
        encodedActivityId: encodeURIComponent(activityId),
        encodedContactId: encodeURIComponent(contactId)
      };

      const url = StringUtil.supplant(endpoint.url, values) || "";
      console.log(`[CombinedActivityService] Fetching status for activity ${activityId}:`, {
        url,
        timestamp: new Date().toISOString()
      });

      try {
        const raw = (await FetchUtil.sendMessage({
          method: endpoint.method,
          url
        }) as (IUserActivityViewModel)[]);

        const result = raw[0];
        console.log(`[CombinedActivityService] Retrieved status for activity ${activityId}:`, {
          hasData: !!result,
          stepCount: result?.steps?.length || 0,
          timestamp: new Date().toISOString()
        });

        return result;
      } catch (error) {
        console.error(`[CombinedActivityService] Error fetching status for activity ${activityId}:`, error);
        return null;
      }
    });

    const results = (await Promise.all(statusPromises))
      .filter((result): result is IUserActivityViewModel => result !== null);

    console.log("[CombinedActivityService] Completed fetching activity statuses:", {
      requestedCount: activityIds.length,
      retrievedCount: results.length,
      timestamp: new Date().toISOString()
    });

    return results;
  }

  private async createActivity(activityId: string, profile: IUserProfile): Promise<void> {
    console.log("[CombinedActivityService] Creating activity:", {
      activityId,
      timestamp: new Date().toISOString()
    });

    const activity = window.CombinedActivity.find((a: Activity) => a.Id === activityId);
    if (!activity) {
      console.error(`[CombinedActivityService] Activity ${activityId} not found in CombinedActivity`);
      return;
    }

    // Create the initial activity record using UserActivityModel
    const userActivity = new UserActivityModel(profile);

    // Update the activity data from CombinedActivity
    userActivity.useractivity.activity.activityid = activity.Id;
    userActivity.useractivity.activity.title = activity.Title;
    userActivity.useractivity.activity.url = activity.Url;
    userActivity.useractivity.activity.expires = activity.Expires;
    userActivity.useractivity.activity.lmsname = activity.LMSName;
    userActivity.useractivity.activity.specialties = activity.Specialties || [];
    userActivity.useractivity.activity.providers = activity.Providers
      .filter(Boolean)
      .map((p: any) => ({ providerid: p.Id, title: p.Title }));
    userActivity.useractivity.activity.topics = activity.Topics || [];
    userActivity.useractivity.activity.collections = activity.Collections || [];

    // Ensure all required fields are present
    const now = new Date().toISOString();
    const userActivityData = {
      firstviewed: now,
      lastviewed: now,
      activity: userActivity.useractivity.activity,
      steps: activity.Steps.map((step: ActivityStep) => ({
        stepid: step.Id,
        title: step.Title,
        type: step.Type,
        complete: false,
        timestamp: null
      })),
      user: {
        contactid: profile.contactid,
        email: profile.email,
        firstname: profile.firstname,
        lastname: profile.lastname
      },
      certificate: {
        credits: 0,
        credittype: "",
        templateid: "",
        credittypeinfo: "",
        date: null,
        data: {},
        certpath: null
      },
      disabled: false,
      hidenotifications: false,
      isExternal: false,
      attestation: false
    };

    const body = {
      activityid: activityId,
      contactid: profile.contactid,
      useractivity: userActivityData
    };

    console.log("[CombinedActivityService] Sending create activity request:", {
      activityId,
      timestamp: new Date().toISOString()
    });

    try {
      await Promise.all([
        FetchUtil.sendMessage(this._api.save, body)
      ]);

      console.log("[CombinedActivityService] Successfully created activity:", {
        activityId,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error("[CombinedActivityService] Error creating activity:", {
        activityId,
        error,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  }

  public async completeActivityStep(activityId: string, stepId: string): Promise<void> {
    console.log("[CombinedActivityService] Completing activity step:", {
      activityId,
      stepId,
      timestamp: new Date().toISOString()
    });

    const profile = await WyanokeUtil.loadUserProfile(true, false);
    if (!profile) {
      console.warn("[CombinedActivityService] No user profile found");
      return;
    }

    // Find the activity and step in CombinedActivity
    const activity = window.CombinedActivity.find((a: Activity) => a.Id === activityId);
    if (!activity) {
      console.error(`[CombinedActivityService] Activity ${activityId} not found in CombinedActivity`);
      return;
    }

    console.log("[CombinedActivityService] Found activity:", {
      activityId,
      activityTitle: activity.Title,
      stepCount: activity.Steps.length,
      timestamp: new Date().toISOString()
    });

    const activityStep = activity.Steps.find((s: ActivityStep) => s.Id === stepId);
    if (!activityStep) {
      console.error(`[CombinedActivityService] Step ${stepId} not found in activity ${activityId}`);
      return;
    }

    console.log("[CombinedActivityService] Found step:", {
      stepId,
      stepTitle: activityStep.Title,
      stepType: activityStep.Type,
      timestamp: new Date().toISOString()
    });

    try {
      // First check if activity exists
      const statuses = await this.getActivityStatus([activityId]);
      const activityStatus = statuses[0];

      // If activity doesn't exist, create it
      if (!activityStatus) {
        console.log("[CombinedActivityService] Activity does not exist, creating it first");
        await this.createActivity(activityId, profile);
      }

      // Create the step object with certificate data
      const step = {
        complete: true,
        stepid: activityStep.Id,
        timestamp: (new Date(Date.now())).toString(),
        title: activityStep.Title,
        type: activityStep.Type,
        certificate: {
          credits: window.Activity?.CreditAmount || 0,
          credittype: window.Activity?.CreditType || "",
          templateid: window.Activity?.CertificateTemplate || "",
          credittypeinfo: window.Activity?.CertificateInfo || "",
          date: new Date().toISOString(),
          data: window.Activity?.FormData || [],
          certpath: null
        }
      };

      console.log("[CombinedActivityService] Step data with certificate:", {
        stepId: step.stepid,
        stepType: step.type,
        certificate: step.certificate
      });

      const body = {
        activityid: activityId,
        contactid: profile.contactid,
        step: step
      };

      console.log("[CombinedActivityService] Sending step completion request:", {
        activityId,
        stepId,
        timestamp: new Date().toISOString(),
        body: JSON.stringify(body, null, 2)
      });

      const response = await FetchUtil.sendMessage(this._api.updatestep, body);
      console.log("[CombinedActivityService] Step completion response:", {
        responseExists: !!response,
        responseType: typeof response,
        responseStringified: JSON.stringify(response, null, 2)
      });

      console.log("[CombinedActivityService] Successfully completed step:", {
        activityId,
        stepId,
        timestamp: new Date().toISOString()
      });
    } catch (error: any) {
      console.error("[CombinedActivityService] Error in completeActivityStep:", {
        activityId,
        stepId,
        error,
        errorMessage: error?.message || 'Unknown error',
        errorStack: error?.stack || 'No stack trace'
      });
      throw error;
    }

    this.resetPromises();
  }

  public async getStepStatus(activityId: string, stepId: string): Promise<boolean> {
    console.log("[CombinedActivityService] Getting step status:", {
      activityId,
      stepId,
      timestamp: new Date().toISOString()
    });

    const statuses = await this.getActivityStatus([activityId]);
    const activityStatus = statuses[0];

    if (!activityStatus) {
      console.warn("[CombinedActivityService] No activity status found:", {
        activityId,
        stepId,
        timestamp: new Date().toISOString()
      });
      return false;
    }

    const step = activityStatus.steps.find(s => s.stepid === stepId);
    const isComplete = step ? step.complete : false;

    console.log("[CombinedActivityService] Retrieved step status:", {
      activityId,
      stepId,
      isComplete,
      timestamp: new Date().toISOString()
    });

    return isComplete;
  }

  private resetPromises(): void {
    console.log("[CombinedActivityService] Resetting promises");
    this._currentStatePromise = null;
    this._updatePromise = null;
  }

  public async saveCreditsForActivities(
    activityIds: string[],
    credits: number,
    creditType: string,
    certTemplate: string,
    creditTypeInfo: string,
    data: ReadonlyArray<{ name: string; value: string }>
  ): Promise<void> {
    console.log("[CombinedActivityService] ===== START saveCreditsForActivities =====");
    console.log("[CombinedActivityService] Input parameters:", {
      activityIds,
      credits,
      creditType,
      certTemplate,
      creditTypeInfo,
      dataLength: data.length,
      data: data
    });

    const profile = await WyanokeUtil.loadUserProfile(true, false);
    if (!profile) {
      console.warn("[CombinedActivityService] No user profile found");
      return;
    }
    console.log("[CombinedActivityService] User profile loaded:", {
      contactId: profile.contactid,
      email: profile.email
    });

    // Save credits for each activity
    for (const activityId of activityIds) {
      console.log(`[CombinedActivityService] ===== Processing activity ${activityId} =====`);

      // Get activity details from CombinedActivity
      const activity = window.CombinedActivity?.find((a: Activity) => a.Id === activityId);
      if (!activity) {
        console.error(`[CombinedActivityService] Activity ${activityId} not found in CombinedActivity`);
        continue;
      }
      console.log("[CombinedActivityService] Found activity:", {
        id: activity.Id,
        title: activity.Title,
        steps: activity.Steps.length
      });

      const now = new Date().toISOString();

      // Format the certificate data to match the working version
      console.log("[CombinedActivityService] ===== START Certificate Data Creation =====");
      console.log("[CombinedActivityService] Raw input data:", {
        activityId,
        credits,
        creditType,
        certTemplate,
        creditTypeInfo,
        dataLength: data.length,
        data: JSON.stringify(data, null, 2)
      });

      // Log each data item before processing
      data.forEach((item, index) => {
        console.log(`[CombinedActivityService] Data item ${index}:`, {
          name: item.name,
          value: item.value,
          type: typeof item.value
        });
      });

      const certificateData = {
        credits: credits,
        credittype: creditType,
        templateid: certTemplate,
        credittypeinfo: creditTypeInfo,
        date: now,
        data: data.map(item => {
          const processedItem = {
            name: item.name,
            value: item.value
          };
          console.log("[CombinedActivityService] Processed data item:", processedItem);
          return processedItem;
        }),
        certpath: null
      };

      console.log("[CombinedActivityService] Final certificate data:", {
        credits: certificateData.credits,
        credittype: certificateData.credittype,
        templateid: certificateData.templateid,
        credittypeinfo: certificateData.credittypeinfo,
        date: certificateData.date,
        dataLength: certificateData.data.length,
        dataSample: certificateData.data.slice(0, 3),
        certpath: certificateData.certpath
      });

      const body = {
        activityid: activityId,
        contactid: profile.contactid,
        useractivity: {
          firstviewed: now,
          lastviewed: now,
          activity: {
            activityid: activity.Id,
            title: activity.Title,
            url: activity.Url,
            expires: activity.Expires,
            lmsname: activity.LMSName,
            specialties: activity.Specialties || [],
            providers: activity.Providers
              .filter(Boolean)
              .map((p: any) => ({ providerid: p.Id, title: p.Title })),
            topics: activity.Topics || [],
            collections: activity.Collections || []
          },
          steps: activity.Steps.map((step: ActivityStep) => ({
            stepid: step.Id,
            title: step.Title,
            type: step.Type,
            complete: false,
            timestamp: null
          })),
          user: {
            contactid: profile.contactid,
            email: profile.email,
            firstname: profile.firstname,
            lastname: profile.lastname
          },
          certificate: certificateData,
          disabled: false,
          hidenotifications: false,
          isExternal: false,
          attestation: false
        }
      };

      try {
        console.log("[CombinedActivityService] ===== START MongoDB Save =====");
        console.log("[CombinedActivityService] API endpoint:", this._api.save);

        // First check if activity exists
        const existingActivity = await this.getActivityStatus([activityId]);
        console.log("[CombinedActivityService] Existing activity check:", {
          exists: !!existingActivity,
          activityCount: existingActivity?.length || 0
        });

        if (existingActivity && existingActivity.length > 0) {
          // Update existing activity
          console.log("[CombinedActivityService] Updating existing activity");
          const existing = existingActivity[0];

          // Find the claim credit step
          const claimCreditStep = activity.Steps.find(step =>
            step.Type === "CME Podcast Episode Claim Credit" ||
            step.Type === "Claim Credit" ||
            step.Type === "Healio Master Class Claim Credit" ||
            step.Type === "Case Care 360 Claim Credit"
          );

          if (!claimCreditStep) {
            console.error("[CombinedActivityService] No claim credit step found in activity");
            throw new Error("No claim credit step found in activity");
          }

          // Create step object with certificate data
          const step = {
            stepid: claimCreditStep.Id,
            title: claimCreditStep.Title,
            type: claimCreditStep.Type,
            complete: true,
            timestamp: now,
            certificate: {
              credits: credits,
              credittype: creditType,
              templateid: certTemplate,
              credittypeinfo: creditTypeInfo,
              date: now,
              data: data.map(item => ({
                name: item.name,
                value: item.value
              })),
              certpath: null
            }
          };

          // First update the step
          const stepUpdateBody = {
            activityid: activityId,
            contactid: profile.contactid,
            step: step
          };

          console.log("[CombinedActivityService] Step update request body:", {
            activityid: stepUpdateBody.activityid,
            contactid: stepUpdateBody.contactid,
            step: {
              stepid: stepUpdateBody.step.stepid,
              type: stepUpdateBody.step.type,
              certificate: stepUpdateBody.step.certificate
            }
          });

          const stepUpdateResponse = await FetchUtil.sendMessage(this._api.updatestep, stepUpdateBody);
          console.log("[CombinedActivityService] Step update response:", {
            responseExists: !!stepUpdateResponse,
            responseType: typeof stepUpdateResponse
          });

          // Then update the certificate
          const certificateUpdateBody = {
            activityid: activityId,
            contactid: profile.contactid,
            certificate: {
              credits: credits,
              credittype: creditType,
              templateid: certTemplate,
              credittypeinfo: creditTypeInfo,
              date: now,
              data: data.map(item => ({
                name: item.name,
                value: item.value
              })),
              certpath: null
            }
          };

          console.log("[CombinedActivityService] Certificate update request body:", {
            activityid: certificateUpdateBody.activityid,
            contactid: certificateUpdateBody.contactid,
            certificate: {
              credits: certificateUpdateBody.certificate.credits,
              credittype: certificateUpdateBody.certificate.credittype,
              templateid: certificateUpdateBody.certificate.templateid,
              credittypeinfo: certificateUpdateBody.certificate.credittypeinfo,
              date: certificateUpdateBody.certificate.date,
              dataLength: certificateUpdateBody.certificate.data.length
            }
          });

          const certificateUpdateResponse = await FetchUtil.sendMessage(this._api.updatecertificate, certificateUpdateBody);
          console.log("[CombinedActivityService] Certificate update response:", {
            responseExists: !!certificateUpdateResponse,
            responseType: typeof certificateUpdateResponse,
            responseData: certificateUpdateResponse
          });
        } else {
          // Create new activity
          console.log("[CombinedActivityService] Creating new activity");
          console.log("[CombinedActivityService] Request body structure:", {
            activityid: body.activityid,
            contactid: body.contactid,
            useractivity: {
              firstviewed: body.useractivity.firstviewed,
              lastviewed: body.useractivity.lastviewed,
              activity: {
                activityid: body.useractivity.activity.activityid,
                title: body.useractivity.activity.title
              },
              certificate: {
                credits: body.useractivity.certificate.credits,
                credittype: body.useractivity.certificate.credittype,
                dataLength: body.useractivity.certificate.data.length
              }
            }
          });

          const response = await FetchUtil.sendMessage(this._api.save, body);
          console.log("[CombinedActivityService] Save response details:", {
            responseExists: !!response,
            responseType: typeof response,
            responseStringified: JSON.stringify(response, null, 2),
            responseKeys: response ? Object.keys(response) : []
          });

          if (!response) {
            console.warn("[CombinedActivityService] No response received from save operation");
          }
        }

        console.log(`[CombinedActivityService] Successfully saved credits for activity ${activityId}`);
        console.log("[CombinedActivityService] ===== END MongoDB Save =====");

        // Handle ACCME submission if enabled
        if (window.Activity && window.Activity.EnableAccmeSubmission) {
          console.log("[CombinedActivityService] Submitting to ACCME");
          await FetchUtil.sendMessage({
            method: "GET",
            url: `/edu/accme/send?activityid=${activityId}&contactid=${profile.contactid}&certifyingboard=${creditType}`
          });
        }
      } catch (error: any) {
        console.error(`[CombinedActivityService] Error saving credits for activity ${activityId}:`, {
          error,
          errorMessage: error?.message || 'Unknown error',
          errorStack: error?.stack || 'No stack trace',
          requestBody: JSON.stringify(body, null, 2)
        });
        throw error;
      }
    }

    console.log("[CombinedActivityService] Completed saving credits for all activities");
  }
}